import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';

import type { ProjectScope } from './project-scope';
import { requireProjectScope } from './project-scope';
import { isSameSerializableValue, stableStringify } from './serializable-comparison';
import { isTerminalWriteStatus, type ProjectWriteOperation, type ProjectWriteResult, type WriteOperationStatus } from './write-operation';

type DrainableWriteStatus = 'pending' | 'retryable';
type CountedWriteStatus = DrainableWriteStatus | 'conflict_review' | 'rejected';
type ReviewWriteStatus = 'retryable' | 'conflict_review' | 'rejected';
type UnresolvedWriteStatus = 'pending' | 'retryable' | 'conflict_review' | 'rejected';

export type ProjectWriteOutboxStatusCounts = {
    pending: number;
    retryable: number;
    conflictReview: number;
    rejected: number;
};

export type ProjectWriteOutboxChange = {
    scope: ProjectScope;
    operationId: string;
};

export type ProjectWriteResultChange = {
    scope: ProjectScope;
    operation: ProjectWriteOperation;
    result: ProjectWriteResult;
};

type ProjectWriteOutboxRecord = {
    operationId: string;
    projectKey: string;
    status: WriteOperationStatus;
    operation: ProjectWriteOperation;
    lastResult?: ProjectWriteResult;
    reason?: string;
    conflictId?: string;
    appliedAtMs?: number;
};

export type ProjectWriteOutboxEntry = {
    operation: ProjectWriteOperation;
    status: WriteOperationStatus;
    lastResult?: ProjectWriteResult;
    reason?: string;
    conflictId?: string;
    appliedAtMs?: number;
};

interface ProjectWriteOutboxDB extends DBSchema {
    projectWrites: {
        key: string;
        value: ProjectWriteOutboxRecord;
        indexes: {
            projectKey: string;
            status: WriteOperationStatus;
            projectStatus: [string, WriteOperationStatus];
        };
    };
}

const DB_NAME = 'project-write-outbox';
const DB_VERSION = 1;
const PROJECT_WRITES_STORE = 'projectWrites';

let dbInstance: IDBPDatabase<ProjectWriteOutboxDB> | null = null;
let dbOpenPromise: Promise<IDBPDatabase<ProjectWriteOutboxDB>> | null = null;
const outboxChangeListeners = new Set<(change: ProjectWriteOutboxChange) => void>();
const resultChangeListeners = new Set<(change: ProjectWriteResultChange) => void>();

function toProjectKey(scope: ProjectScope): string {
    const checked = requireProjectScope(scope);
    return `${checked.orgId}/${checked.projectId}`;
}

function isSameCapturedOperation(left: ProjectWriteOperation, right: ProjectWriteOperation): boolean {
    return isSameSerializableValue(left, right);
}

function isUnresolvedWriteStatus(status: WriteOperationStatus): status is UnresolvedWriteStatus {
    return status === 'pending'
        || status === 'retryable'
        || status === 'conflict_review'
        || status === 'rejected';
}

function isSameUnresolvedWriteIntent(record: ProjectWriteOutboxRecord, operation: ProjectWriteOperation): boolean {
    if (!isUnresolvedWriteStatus(record.status)) return false;

    const existing = record.operation;
    return existing.actorId === operation.actorId
        && existing.feature === operation.feature
        && existing.capability === operation.capability
        && existing.conflictUnit === operation.conflictUnit
        && existing.kind === operation.kind
        && stableStringify(existing.payload) === stableStringify(operation.payload);
}

async function hasUnresolvedDuplicateWriteIntent(
    db: IDBPDatabase<ProjectWriteOutboxDB>,
    scope: ProjectScope,
    operation: ProjectWriteOperation,
): Promise<boolean> {
    const records = await db.getAllFromIndex(PROJECT_WRITES_STORE, 'projectKey', toProjectKey(scope));
    return records.some((record) => isSameUnresolvedWriteIntent(record, operation));
}

function createDuplicateOperationError(operationId: string): Error {
    return new Error(`Duplicate project write operation id with different operation: ${operationId}`);
}

function toOutboxEntry(record: ProjectWriteOutboxRecord): ProjectWriteOutboxEntry {
    return {
        operation: record.operation,
        status: record.status,
        lastResult: record.lastResult,
        reason: record.reason,
        conflictId: record.conflictId,
        appliedAtMs: record.appliedAtMs,
    };
}

function notifyProjectWriteEnqueued(change: ProjectWriteOutboxChange): void {
    for (const listener of outboxChangeListeners) {
        listener(change);
    }
}

function notifyProjectWriteResult(change: ProjectWriteResultChange): void {
    for (const listener of resultChangeListeners) {
        listener(change);
    }
}

export function subscribeToProjectWriteOutboxChanges(
    listener: (change: ProjectWriteOutboxChange) => void,
): () => void {
    outboxChangeListeners.add(listener);
    return () => {
        outboxChangeListeners.delete(listener);
    };
}

export function subscribeToProjectWriteResultChanges(
    listener: (change: ProjectWriteResultChange) => void,
): () => void {
    resultChangeListeners.add(listener);
    return () => {
        resultChangeListeners.delete(listener);
    };
}

function isConstraintError(error: unknown): boolean {
    return error instanceof DOMException
        ? error.name === 'ConstraintError'
        : typeof error === 'object'
            && error !== null
            && 'name' in error
            && error.name === 'ConstraintError';
}

async function getProjectWriteOutboxDB(): Promise<IDBPDatabase<ProjectWriteOutboxDB>> {
    if (dbInstance) {
        return dbInstance;
    }

    if (dbOpenPromise) {
        return dbOpenPromise;
    }

    dbOpenPromise = openDB<ProjectWriteOutboxDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(PROJECT_WRITES_STORE)) {
                const store = db.createObjectStore(PROJECT_WRITES_STORE, {
                    keyPath: 'operationId',
                });
                store.createIndex('projectKey', 'projectKey');
                store.createIndex('status', 'status');
                store.createIndex('projectStatus', ['projectKey', 'status']);
            }
        },
    });

    dbInstance = await dbOpenPromise;
    dbOpenPromise = null;

    return dbInstance;
}

async function listWritesByStatuses(
    scope: ProjectScope,
    statuses: readonly WriteOperationStatus[],
): Promise<ProjectWriteOperation[]> {
    const records = await listWriteRecordsByStatuses(scope, statuses);
    return records.map((record) => record.operation);
}

async function listWriteEntriesByStatuses(
    scope: ProjectScope,
    statuses: readonly WriteOperationStatus[],
): Promise<ProjectWriteOutboxEntry[]> {
    const records = await listWriteRecordsByStatuses(scope, statuses);
    return records.map(toOutboxEntry);
}

async function listWriteRecordsByStatuses(
    scope: ProjectScope,
    statuses: readonly WriteOperationStatus[],
): Promise<ProjectWriteOutboxRecord[]> {
    const db = await getProjectWriteOutboxDB();
    const projectKey = toProjectKey(scope);
    const recordsByStatus: ProjectWriteOutboxRecord[] = [];

    for (const status of statuses) {
        const records = await db.getAllFromIndex(PROJECT_WRITES_STORE, 'projectStatus', [projectKey, status]);
        recordsByStatus.push(...records);
    }

    return recordsByStatus;
}

export async function storeProjectWriteIntent(operation: ProjectWriteOperation): Promise<void> {
    const db = await getProjectWriteOutboxDB();
    const scope = requireProjectScope(operation.scope);
    const capturedOperation = {
        ...operation,
        scope,
    };
    const existing = await db.get(PROJECT_WRITES_STORE, operation.id);

    if (existing) {
        if (isSameCapturedOperation(existing.operation, capturedOperation)) {
            return;
        }

        throw createDuplicateOperationError(operation.id);
    }

    if (await hasUnresolvedDuplicateWriteIntent(db, scope, capturedOperation)) {
        return;
    }

    try {
        await db.add(PROJECT_WRITES_STORE, {
            operationId: operation.id,
            projectKey: toProjectKey(scope),
            status: 'pending',
            operation: capturedOperation,
        });
        notifyProjectWriteEnqueued({ scope, operationId: operation.id });
    } catch (error) {
        if (!isConstraintError(error)) {
            throw error;
        }

        const racedExisting = await db.get(PROJECT_WRITES_STORE, operation.id);
        if (racedExisting && isSameCapturedOperation(racedExisting.operation, capturedOperation)) {
            return;
        }

        throw createDuplicateOperationError(operation.id);
    }
}

/** @deprecated Use storeProjectWriteIntent for local IndexedDB intent storage. */
export const enqueueProjectWrite = storeProjectWriteIntent;

export async function listPendingProjectWrites(scope: ProjectScope): Promise<ProjectWriteOperation[]> {
    const statuses: readonly DrainableWriteStatus[] = ['pending', 'retryable'];
    return listWritesByStatuses(scope, statuses);
}

export async function getProjectWriteOutboxEntry(operationId: string): Promise<ProjectWriteOutboxEntry | null> {
    const db = await getProjectWriteOutboxDB();
    const record = await db.get(PROJECT_WRITES_STORE, operationId);
    return record ? toOutboxEntry(record) : null;
}

export async function listProjectWriteReviewEntries(scope: ProjectScope): Promise<ProjectWriteOutboxEntry[]> {
    const statuses: readonly ReviewWriteStatus[] = ['conflict_review', 'retryable', 'rejected'];
    const entries = await listWriteEntriesByStatuses(scope, statuses);
    return entries.toSorted((left, right) => {
        const leftProject = toProjectKey(left.operation.scope);
        const rightProject = toProjectKey(right.operation.scope);
        if (leftProject !== rightProject) {
            return leftProject.localeCompare(rightProject);
        }
        if (left.operation.feature !== right.operation.feature) {
            return left.operation.feature.localeCompare(right.operation.feature);
        }
        return left.operation.createdAtMs - right.operation.createdAtMs
            || left.operation.id.localeCompare(right.operation.id);
    });
}

export async function listProjectWriteStatusCounts(scope: ProjectScope): Promise<ProjectWriteOutboxStatusCounts> {
    const statuses: readonly CountedWriteStatus[] = ['pending', 'retryable', 'conflict_review', 'rejected'];
    const records = await listWriteRecordsByStatuses(scope, statuses);

    return records.reduce<ProjectWriteOutboxStatusCounts>((counts, record) => {
        if (record.status === 'conflict_review') {
            counts.conflictReview += 1;
        } else if (record.status === 'pending' || record.status === 'retryable' || record.status === 'rejected') {
            counts[record.status] += 1;
        }
        return counts;
    }, {
        pending: 0,
        retryable: 0,
        conflictReview: 0,
        rejected: 0,
    });
}

export async function markProjectWriteResult(result: ProjectWriteResult): Promise<void> {
    const db = await getProjectWriteOutboxDB();
    const record = await db.get(PROJECT_WRITES_STORE, result.operationId);

    if (!record) {
        return;
    }

    if (isTerminalWriteStatus(record.status) && result.status === 'retryable') {
        return;
    }

    if (record.status === 'applied' || record.status === 'superseded') {
        return;
    }

    await db.put(PROJECT_WRITES_STORE, {
        ...record,
        status: result.status,
        lastResult: result,
        reason: result.reason,
        conflictId: result.conflictId,
        appliedAtMs: result.appliedAtMs,
    });
    notifyProjectWriteResult({ scope: record.operation.scope, operation: record.operation, result });
}

export async function requeueProjectWrite(operationId: string, reason = 'manual-review-requeue'): Promise<void> {
    const db = await getProjectWriteOutboxDB();
    const record = await db.get(PROJECT_WRITES_STORE, operationId);

    if (!record) {
        return;
    }

    if (record.status !== 'retryable') {
        return;
    }

    await db.put(PROJECT_WRITES_STORE, {
        ...record,
        status: 'pending',
        lastResult: undefined,
        reason,
        conflictId: undefined,
        appliedAtMs: undefined,
    });
    notifyProjectWriteEnqueued({ scope: record.operation.scope, operationId });
}

function supersededWriteRecord(record: ProjectWriteOutboxRecord, reason: string): ProjectWriteOutboxRecord {
    const result: ProjectWriteResult = {
        operationId: record.operationId,
        status: 'superseded',
        reason,
    };

    return {
        ...record,
        status: 'superseded',
        lastResult: result,
        reason,
        conflictId: undefined,
        appliedAtMs: undefined,
    };
}

export async function supersedeProjectWrite(
    operationId: string,
    reason: string,
    expectedStatuses: readonly WriteOperationStatus[] = ['conflict_review', 'rejected'],
): Promise<void> {
    const db = await getProjectWriteOutboxDB();
    const record = await db.get(PROJECT_WRITES_STORE, operationId);

    if (!record || !expectedStatuses.includes(record.status)) {
        return;
    }

    const superseded = supersededWriteRecord(record, reason);
    await db.put(PROJECT_WRITES_STORE, superseded);
    notifyProjectWriteResult({
        scope: record.operation.scope,
        operation: record.operation,
        result: superseded.lastResult!,
    });
}

export async function supersedeProjectWritesForConflictUnit(input: {
    scope: ProjectScope;
    conflictUnit: string;
    reason: string;
    kinds?: readonly ProjectWriteOperation['kind'][];
    expectedStatuses?: readonly WriteOperationStatus[];
}): Promise<void> {
    const db = await getProjectWriteOutboxDB();
    const expectedStatuses: readonly WriteOperationStatus[] = input.expectedStatuses ?? ['pending', 'retryable'];
    const records = await listWriteRecordsByStatuses(input.scope, expectedStatuses);
    const matchingRecords = records.filter((record) => (
        record.operation.conflictUnit === input.conflictUnit
        && (!input.kinds || input.kinds.includes(record.operation.kind))
    ));

    await Promise.all(matchingRecords.map(async (record) => {
        const superseded = supersededWriteRecord(record, input.reason);
        await db.put(PROJECT_WRITES_STORE, superseded);
        notifyProjectWriteResult({
            scope: record.operation.scope,
            operation: record.operation,
            result: superseded.lastResult!,
        });
    }));
}

export async function listConflictReviewWrites(scope: ProjectScope): Promise<ProjectWriteOperation[]> {
    return listWritesByStatuses(scope, ['conflict_review']);
}

export async function listConflictReviewWriteEntries(scope: ProjectScope): Promise<ProjectWriteOutboxEntry[]> {
    return listWriteEntriesByStatuses(scope, ['conflict_review']);
}

export async function listRejectedWrites(scope: ProjectScope): Promise<ProjectWriteOperation[]> {
    return listWritesByStatuses(scope, ['rejected']);
}

export async function listRejectedWriteEntries(scope: ProjectScope): Promise<ProjectWriteOutboxEntry[]> {
    return listWriteEntriesByStatuses(scope, ['rejected']);
}

export async function listRetryableWriteEntries(scope: ProjectScope): Promise<ProjectWriteOutboxEntry[]> {
    return listWriteEntriesByStatuses(scope, ['retryable']);
}

/** @internal - close the DB and reset the singleton for test isolation. */
export function _resetForTesting(): void {
    if (dbInstance) {
        dbInstance.close();
        dbInstance = null;
    }
    dbOpenPromise = null;
    resultChangeListeners.clear();
}
